import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import {
  PageTitle, Badge, Button, Card, EmptyState, Icons,
} from '../shared/ui';

const STATUS_TONE = { verified: 'success', rejected: 'danger', pending: 'warning' };

const SELECT_CLS = 'h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';
const INPUT_CLS  = 'w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:bg-stone-50 disabled:text-stone-400';

const getStatusLabel = (status, method) => {
  if (status === 'verified') {
    if (method === 'ocr')   return 'Verified (OCR)';
    if (method === 'admin') return 'Verified (Admin)';
    return 'Verified';
  }
  if (status === 'rejected') return 'Rejected';
  return 'Pending Review';
};

const MeterUpload = () => {
  const queryClient = useQueryClient();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [electricReading, setElectricReading] = useState('');
  const [waterReading, setWaterReading]       = useState('');
  const [electricPhoto, setElectricPhoto]     = useState(null);
  const [waterPhoto, setWaterPhoto]           = useState(null);
  const [electricPreview, setElectricPreview] = useState(null);
  const [waterPreview, setWaterPreview]       = useState(null);
  const [notes, setNotes]                     = useState('');
  const [showSuccess, setShowSuccess]         = useState(false);
  const [lightboxImage, setLightboxImage]     = useState(null);
  const [historyFilter, setHistoryFilter]     = useState('all');

  const electricInputRef = useRef(null);
  const waterInputRef    = useRef(null);

  const { data: readings = [] } = useQuery({
    queryKey: ['renter-meter-readings'],
    queryFn: async () => {
      const response = await api.get('/api/renters/meter-readings');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : [],
  });

  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['renter-meter-history', historyFilter],
    queryFn: async () => {
      const params = historyFilter !== 'all' ? { reading_type: historyFilter } : {};
      const response = await api.get('/api/renters/meter-readings/history', { params });
      return response.data;
    },
  });

  const { data: roomInfo } = useQuery({
    queryKey: ['renter-my-room'],
    queryFn: async () => {
      const response = await api.get('/api/renters/my-room');
      return response.data;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await api.post('/api/renters/meter-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renter-meter-readings'] });
      resetForm();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    },
    onError: (error) => {
      alert('Failed to upload meter reading: ' + (error.response?.data?.detail || error.message));
    },
  });

  const resetForm = () => {
    setElectricReading('');
    setWaterReading('');
    setElectricPhoto(null);
    setWaterPhoto(null);
    setElectricPreview(null);
    setWaterPreview(null);
    setNotes('');
  };

  const handlePhotoSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'electric') { setElectricPhoto(file); setElectricPreview(URL.createObjectURL(file)); }
      else                     { setWaterPhoto(file);    setWaterPreview(URL.createObjectURL(file)); }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!electricReading && !waterReading) { alert('Please enter at least one meter reading'); return; }
    const formData = new FormData();
    formData.append('month', selectedMonth);
    if (electricReading) formData.append('electric_reading', electricReading);
    if (waterReading)    formData.append('water_reading', waterReading);
    if (electricPhoto)   formData.append('electric_photo', electricPhoto);
    if (waterPhoto)      formData.append('water_photo', waterPhoto);
    if (notes)           formData.append('notes', notes);
    uploadMutation.mutate(formData);
  };

  const monthReadings    = readings?.filter(r => r.month === selectedMonth) || [];
  const hasElectricReading = monthReadings.some(r => r.reading_type === 'electricity');
  const hasWaterReading    = monthReadings.some(r => r.reading_type === 'water');

  return (
    <div className="space-y-6">
      <PageTitle
        title="Meter Readings"
        subtitle={roomInfo ? `Upload monthly readings · Room ${roomInfo.room_number}` : 'Upload your monthly electricity and water meter readings'}
      />

      {/* Success banner */}
      {showSuccess && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <Icons.CheckCircle2 size={20} className="text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">Meter reading uploaded successfully!</p>
            <p className="text-xs text-green-600">Your reading will be verified by the admin.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Form */}
        <Card>
          <div className="px-5 py-3.5 border-b border-stone-100 flex items-center gap-2">
            <Icons.Upload size={16} className="text-teal-600" />
            <p className="text-sm font-semibold text-stone-900">Submit New Reading</p>
          </div>
          <div className="p-5">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Month */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Month</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

              {/* Electricity */}
              <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50">
                <div className="flex items-center gap-2 mb-3">
                  <Icons.Zap size={16} className="text-yellow-600" />
                  <span className="text-sm font-medium text-stone-800">Electricity Meter</span>
                  {hasElectricReading && <Badge tone="warning">Already submitted</Badge>}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Current Reading (kWh)</label>
                    <input type="number" step="0.01" value={electricReading} onChange={(e) => setElectricReading(e.target.value)} placeholder="e.g., 1234.56" className={INPUT_CLS} disabled={hasElectricReading} />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Photo Proof</label>
                    <input ref={electricInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => handlePhotoSelect(e, 'electric')} className="hidden" disabled={hasElectricReading} />
                    <button type="button" onClick={() => electricInputRef.current?.click()} disabled={hasElectricReading} className="w-full border-2 border-dashed border-stone-200 rounded-lg p-4 hover:border-yellow-400 transition-colors disabled:opacity-50">
                      {electricPreview ? (
                        <img src={electricPreview} alt="Electric meter" className="max-h-32 mx-auto rounded" />
                      ) : (
                        <div className="text-center text-stone-400">
                          <Icons.Camera size={28} className="mx-auto mb-1" />
                          <p className="text-xs">Click to take photo or upload</p>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Water */}
              <div className="border border-sky-200 rounded-xl p-4 bg-sky-50">
                <div className="flex items-center gap-2 mb-3">
                  <Icons.Droplets size={16} className="text-sky-600" />
                  <span className="text-sm font-medium text-stone-800">Water Meter</span>
                  {hasWaterReading && <Badge tone="info">Already submitted</Badge>}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Current Reading (m³)</label>
                    <input type="number" step="0.01" value={waterReading} onChange={(e) => setWaterReading(e.target.value)} placeholder="e.g., 567.89" className={INPUT_CLS} disabled={hasWaterReading} />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Photo Proof</label>
                    <input ref={waterInputRef} type="file" accept="image/*" capture="environment" onChange={(e) => handlePhotoSelect(e, 'water')} className="hidden" disabled={hasWaterReading} />
                    <button type="button" onClick={() => waterInputRef.current?.click()} disabled={hasWaterReading} className="w-full border-2 border-dashed border-stone-200 rounded-lg p-4 hover:border-sky-400 transition-colors disabled:opacity-50">
                      {waterPreview ? (
                        <img src={waterPreview} alt="Water meter" className="max-h-32 mx-auto rounded" />
                      ) : (
                        <div className="text-center text-stone-400">
                          <Icons.Camera size={28} className="mx-auto mb-1" />
                          <p className="text-xs">Click to take photo or upload</p>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">Notes (optional)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Any additional notes..." className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 resize-none" />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button kind="primary" type="submit" loading={uploadMutation.isPending} disabled={!electricReading && !waterReading} iconLeft={<Icons.Upload size={14} />} className="flex-1 justify-center">
                  Submit Reading
                </Button>
                <Button kind="ghost" type="button" onClick={resetForm}>Reset</Button>
              </div>
            </form>
          </div>
        </Card>

        {/* History */}
        <Card>
          <div className="px-5 py-3.5 border-b border-stone-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icons.Chart size={16} className="text-stone-500" />
              <p className="text-sm font-semibold text-stone-900">Reading History</p>
            </div>
            <select value={historyFilter} onChange={(e) => setHistoryFilter(e.target.value)} className={SELECT_CLS}>
              <option value="all">All Types</option>
              <option value="electricity">Electricity</option>
              <option value="water">Water</option>
            </select>
          </div>
          <div className="p-5">
            {/* Room info */}
            {historyData?.room && (
              <div className="mb-4 flex items-center gap-2 text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2">
                <Icons.Building size={12} className="text-stone-400 shrink-0" />
                <span className="font-medium text-stone-700">{historyData.room.building_name}</span>
                <span>·</span>
                <span>Room {historyData.room.room_number}</span>
                <span>·</span>
                <span>Floor {historyData.room.floor}</span>
              </div>
            )}

            {/* Utility rates */}
            {historyData?.utility_pricing && Object.keys(historyData.utility_pricing).length > 0 && (
              <div className="mb-4 p-3 bg-stone-50 rounded-lg text-xs text-stone-600 border border-stone-200">
                <p className="font-medium text-stone-700 mb-1">Current Rates:</p>
                <div className="flex flex-wrap gap-3">
                  {historyData.utility_pricing.electricity && (
                    <span>⚡ Electricity: {historyData.utility_pricing.electricity.price?.toLocaleString()} VND/{historyData.utility_pricing.electricity.unit || 'kWh'}</span>
                  )}
                  {historyData.utility_pricing.water && (
                    <span>💧 Water: {historyData.utility_pricing.water.price?.toLocaleString()} VND/{historyData.utility_pricing.water.unit || 'm³'}</span>
                  )}
                </div>
              </div>
            )}

            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Icons.Spinner size={28} className="animate-spin text-teal-600" />
                <p className="text-sm text-stone-400">Loading history...</p>
              </div>
            ) : !historyData?.readings?.length ? (
              <EmptyState icon={<Icons.Document size={32} />} title="No Readings Yet" description="No readings submitted yet" />
            ) : (
              <div className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
                {historyData.readings.map((reading) => (
                  <div
                    key={reading.id}
                    className={`rounded-xl border p-4 ${
                      reading.reading_type === 'electricity' ? 'bg-yellow-50 border-yellow-200' : 'bg-sky-50 border-sky-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {reading.reading_type === 'electricity'
                          ? <Icons.Zap size={16} className="text-yellow-600" />
                          : <Icons.Droplets size={16} className="text-sky-600" />
                        }
                        <span className="text-sm font-medium text-stone-800 capitalize">{reading.reading_type}</span>
                        <span className="text-stone-300">·</span>
                        <span className="text-xs text-stone-500">{reading.month}</span>
                      </div>
                      <Badge tone={STATUS_TONE[reading.status] || 'neutral'}>
                        {getStatusLabel(reading.status, reading.verification_method)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {[
                        { label: 'Previous', value: reading.previous_reading?.toLocaleString() || '0' },
                        { label: 'Current',  value: (reading.adjusted_reading || reading.reading_value)?.toLocaleString() },
                      ].map(({ label, value }) => (
                        <div key={label} className="p-2 bg-white rounded-lg">
                          <span className="text-xs text-stone-400 block">{label}</span>
                          <span className="font-medium text-stone-900">{value}</span>
                        </div>
                      ))}
                      <div className="p-2 bg-white rounded-lg">
                        <span className="text-xs text-stone-400 block">Usage</span>
                        <span className="font-bold text-stone-900">{reading.usage_amount?.toLocaleString() || '0'}</span>
                        <span className="text-xs text-stone-400 ml-1">{reading.reading_type === 'electricity' ? 'kWh' : 'm³'}</span>
                      </div>
                      <div className="p-2 bg-white rounded-lg">
                        <span className="text-xs text-stone-400 block">Cost</span>
                        <span className="font-bold text-green-700">{reading.total_cost?.toLocaleString() || '0'}</span>
                        <span className="text-xs text-stone-400 ml-1">VND</span>
                      </div>
                    </div>

                    {reading.unit_price > 0 && (
                      <p className="mt-2 text-xs text-stone-400">Rate: {reading.unit_price?.toLocaleString()} VND per {reading.reading_type === 'electricity' ? 'kWh' : 'm³'}</p>
                    )}

                    {reading.admin_note && (
                      <div className="mt-3 p-2 bg-white rounded-lg border-l-4 border-sky-400">
                        <span className="text-xs text-stone-400 block">Admin Note:</span>
                        <p className="text-sm text-stone-700">{reading.admin_note}</p>
                      </div>
                    )}

                    {reading.verified && reading.verified_by_name && (
                      <p className="mt-2 text-xs text-green-600">
                        ✓ Verified by {reading.verified_by_name}
                        {reading.verified_at && ` on ${reading.verified_at.split('T')[0]}`}
                      </p>
                    )}

                    {reading.image_url && (
                      <div className="mt-3">
                        <button onClick={() => setLightboxImage(reading.image_url)} className="group relative inline-block">
                          <img src={reading.image_url} alt="Meter reading proof" className="h-20 w-auto rounded-lg shadow cursor-pointer hover:opacity-80 transition-opacity" />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 rounded-lg transition-all">
                            <Icons.Eye size={20} className="text-white opacity-0 group-hover:opacity-100" />
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightboxImage(null)}>
          <div className="relative max-w-4xl max-h-full">
            <button onClick={() => setLightboxImage(null)} className="absolute -top-10 right-0 text-white/70 hover:text-white p-1">
              <Icons.X size={28} />
            </button>
            <img src={lightboxImage} alt="Meter reading" className="max-w-full max-h-[80vh] rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MeterUpload;
